import * as React from 'react';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {RefObject} from 'react';
import {IconButton, InputAdornment, TextField} from '@mui/material';
import {
  isNotUndefined,
  voidOp,
  isNotNull,
  isNotNil,
  isNil,
} from '@practicegenius/pg-util/src/TypescriptUtils';

type IconComponent = typeof ErrorOutlineIcon;

type Props = Omit<React.ComponentProps<typeof TextField>, 'error'> & {
  id?: string | number;
  label?: string;
  name?: string;
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  helperText?: React.ReactNode;
  error?: string | null;
  hasError?: boolean;
  success?: boolean;
  icon?: IconComponent;
  disabled?: boolean;
  iconClick?: React.MouseEventHandler;
  innerRef?: RefObject<unknown>;
  type?: string;
};

function hasError(props: Props): boolean {
  const {error, hasError = Boolean(error)} = props;
  return hasError;
}

function getInputIcon(props: Props): IconComponent | null {
  const {success, icon} = props;

  switch (true) {
    case hasError(props):
      return ErrorOutlineIcon;
    case success:
      return CheckIcon;
    default:
      return isNotUndefined(icon) ? icon : null;
  }
}

export const Input: React.FC<Props> = ({
  iconClick = voidOp,
  ...props
}: Props) => {
  const {id, helperText, error, hasError: _, innerRef, ...otherProps} = props;
  const InputProps: React.ComponentProps<typeof TextField>['InputProps'] = {
    ...props.InputProps,
  };
  const Icon = getInputIcon(props);

  if (isNotNull(Icon) && isNil(InputProps.endAdornment)) {
    InputProps.endAdornment = (
      <IconButton onClick={iconClick}>
        <Icon color={hasError(props) ? 'error' : 'action'} />
      </IconButton>
    );
  }

  if (isNotNil(InputProps.endAdornment)) {
    InputProps.endAdornment = (
      <InputAdornment position="end">{InputProps.endAdornment}</InputAdornment>
    );
  }

  const fieldId = isNotNil(id)
    ? id.toString()
    : `${otherProps.name || ''}_input`;

  return (
    <TextField
      inputRef={innerRef}
      margin="dense"
      size="small"
      fullWidth
      {...otherProps}
      InputProps={InputProps}
      helperText={error || helperText}
      error={hasError(props)}
      id={fieldId}
    />
  );
};
